// Flex-E Simulatie Methodiek Document
// Versie 1.1 - Greenvision
//
// 1.1 (8 september 2026): hoofdstuk 2.2 herschreven. Het document beloofde op de oude
// regels 49-52 "Indien data in kWh wordt aangeleverd, converteert de tool naar kW:
// kW = kWh x 4", maar die conversie bestond nergens in de keten -- de parser nam de
// kolomwaarde rauw over (useFlexEUpload.ts:103 en :187, `parseFloat(loadStr)`), en over
// upload, parse en simulatie samen stond geen enkele `* 4`, `/ 0.25`, `isKwh` of `unit`.
// De conversie is nu gebouwd en de eenheid wordt bij het uploaden vastgesteld; dit document
// beschrijft vanaf 1.1 wat de tool feitelijk doet, inclusief wat dat voor oudere runs betekent.

export const FLEXE_METHODOLOGY_VERSION = '1.1';

export const getFlexEMethodologyMarkdown = (simulationData?: {
  contractLimit?: number;
  battKw?: number;
  battKwh?: number;
  windowStart?: string;
  windowEnd?: string;
  successRate?: number;
  baselineSuccessRate?: number;
  /** Eenheid waarin de belastingkolom is gelezen (`FLEXE_UNIT_LABEL` uit useFlexEUpload). */
  loadUnit?: string;
}) => {
  const contractLimit = simulationData?.contractLimit || 150;
  const battKw = simulationData?.battKw || 50;
  const battKwh = simulationData?.battKwh || 100;
  const windowStart = simulationData?.windowStart || '16:00';
  const windowEnd = simulationData?.windowEnd || '21:00';
  // Niet verzinnen: de eenheid wordt vandaag nog niet bij de simulatie opgeslagen
  // (`flexe-save-upload/index.ts:58` leest het veld niet uit), dus zonder meegegeven
  // waarde staat er dat het niet is vastgelegd.
  const loadUnit = simulationData?.loadUnit || 'niet vastgelegd bij deze simulatie (zie 7.2)';

  return `# Flex-E Simulatie Methodiek
## Greenvision Energy Solutions | Documentversie ${FLEXE_METHODOLOGY_VERSION}

---

## 1. Inleiding

### 1.1 Doel van de Flex-E Simulatie
De Flex-E simulatietool analyseert historisch energieverbruik om te bepalen of een batterijsysteem in staat is om tijdens piekuren het verbruik onder de contractlimiet te houden. Dit is essentieel voor:

- **Kostenreductie**: Vermijden van boetes voor overschrijding van contractueel afgesproken vermogen
- **Netcongestie**: Bijdragen aan stabilisatie van het elektriciteitsnet tijdens piekuren
- **Investeeringsonderbouwing**: Objectieve analyse voor batterij-investeringsbeslissingen

### 1.2 Werkingsprincipe
Tijdens piekuren (typisch ${windowStart} - ${windowEnd}) geldt een beperkte contractlimiet. Wanneer het daadwerkelijke verbruik deze limiet overschrijdt, moet de batterij het verschil compenseren.

---

## 2. Data Input & Verwerking

### 2.1 Brondata
- **Formaat**: Excel-bestand met kwartierdata
- **Interval**: 15-minuten meetwaarden
- **Kolommen**: Timestamp, Verbruik -- in kW of in kWh per kwartier (de eenheid wordt bij het uploaden vastgesteld, zie 2.2)
- **Periode**: Minimaal 1 maand historische data (idealiter 1 jaar)

### 2.2 Eenheid en dataconversie
De tool rekent in kW. In welke eenheid het bronbestand staat, wordt bij het uploaden **vastgesteld** en nooit uit de waarden afgeleid: een gebouw met een piek van 100 kW en een gebouw met kwartierwaarden van 100 kWh leveren exact dezelfde kolom op.

De eenheid komt, in deze volgorde, uit:
1. de expliciete keuze van de gebruiker bij het uploaden;
2. de eenheid die het bronbestand zelf in de kolomkop vermeldt, bijvoorbeeld *Piekbelasting (kW)*;
3. een bevestiging die de tool vraagt zodra de kolomkop geen eenheid noemt.

Is de eenheid langs geen van deze drie wegen vast te stellen, dan wordt het bestand **niet** ingelezen.

Staat de data in kWh per kwartier, dan geldt:
\`\`\`
kW = kWh × 4 (kwartierwaarde × 4 = momentaan vermogen)
\`\`\`

**Eenheid van deze simulatie:** ${loadUnit}

> **Waarom dit zwaar weegt.** Op een doorgerekende dag met een contractlimiet van 135 kW en een venster van vijf uur levert dezelfde kolom, gelezen als kW, een energietekort van 102,98 kWh en een piektekort van 48,35 kW op. Diezelfde kolom gelezen als kWh per kwartier levert 2.428,62 kWh en 598,40 kW: 23,6× respectievelijk 12,4× hoger. Met een batterij van 200 kW / 400 kWh is het eerste oordeel "voldoende" en het tweede "onvoldoende". De eenheid bepaalt dus de uitkomst, en daarmee de batterijkeuze.

### 2.3 Tijdvenster Filtering
Alleen meetpunten binnen het geconfigureerde piekuren-venster worden meegenomen in de berekening:
- **Standaard venster**: ${windowStart} - ${windowEnd}
- **Werkdagen**: Maandag t/m vrijdag (configureerbaar)

---

## 3. Berekeningen & Formules

### 3.1 Energiebehoefte per Dag (kWh)
Voor elk kwartier waarin het verbruik de contractlimiet overschrijdt:

\`\`\`
Deficit_kwartier = MAX(0, Verbruik_kW - Contractlimiet_kW) × 0,25 uur
\`\`\`

**Dagelijkse totaal:**
\`\`\`
Deficit_dag (kWh) = Σ Deficit_kwartier
\`\`\`

**Voorbeeld:**
Als het verbruik op een kwartier 200 kW is en de contractlimiet ${contractLimit} kW:
\`\`\`
Deficit = (200 - ${contractLimit}) × 0,25 = ${((200 - contractLimit) * 0.25).toFixed(2)} kWh
\`\`\`

### 3.2 Maximale Vermogensoverschrijding per Dag (kW)
Het hoogste momentane vermogen boven de contractlimiet:

\`\`\`
Max_Deficit_kW = MAX(Verbruik_kwartier - Contractlimiet)
\`\`\`

Dit bepaalt of de batterij voldoende ontlaadvermogen heeft.

### 3.3 Pass/Fail Criteria
Een dag wordt als **geslaagd** gemarkeerd als aan BEIDE voorwaarden wordt voldaan:

| Criterium | Voorwaarde | Betekenis |
|-----------|------------|-----------|
| Energie | Deficit_dag ≤ Batterijcapaciteit (kWh) | Batterij heeft voldoende opslag |
| Vermogen | Max_Deficit_kW ≤ Batterijvermogen (kW) | Batterij kan snel genoeg ontladen |

**Formule:**
\`\`\`
Pass = (Deficit_dag ≤ ${battKwh} kWh) AND (Max_Deficit_kW ≤ ${battKw} kW)
\`\`\`

### 3.4 Success Rate
\`\`\`
Success Rate (%) = (Aantal geslaagde dagen / Totaal aantal dagen) × 100
\`\`\`

---

## 4. Grafiekuitleg

### 4.1 Grafiek 1: Baseline vs. Scenario (Lijndiagram)

**Doel:** Visualiseert het effect van een scenario (nieuwe/vervangende machines) op de success rate.

| Element | Beschrijving |
|---------|--------------|
| X-as | Datum (chronologisch) |
| Y-as | Cumulatieve success rate (%) |
| Rode lijn | Baseline: situatie ZONDER scenario-aanpassingen |
| Groene lijn | Met scenario: situatie MÉT nieuwe/vervangende machines |
| Groen gebied | Verschil = directe impact van het scenario |

**Interpretatie:**
- Hoe groter het groene gebied, hoe effectiever de investering
- Convergeren de lijnen? Dan heeft het scenario minder impact op latere dagen
- 100% referentielijn toont het ideale scenario

**Communicatie naar klant:**
> "Zonder de voorgestelde aanpassingen zou u slechts op X% van de dagen voldoende capaciteit hebben. Met onze oplossing stijgt dit naar Y%."

---

### 4.2 Grafiek 2: Dagelijkse Energiebehoefte (Staafdiagram)

**Doel:** Toont hoeveel kWh de batterij dagelijks moet leveren.

| Element | Beschrijving |
|---------|--------------|
| X-as | Datum |
| Y-as | Energiebehoefte (kWh) |
| Staven | Dagelijkse energiebehoefte |
| Rode stippellijn | Batterijcapaciteit (${battKwh} kWh) |

**Kritieke observaties:**
- **Staven ONDER de lijn**: Batterijcapaciteit is voldoende
- **Staven BOVEN de lijn**: Batterij komt energie tekort - klant komt zonder stroom

**Waarschuwing:**
⚠️ Zelfs één dag boven de referentielijn betekent dat de batterij die dag tekortschiet en de contractlimiet wordt overschreden.

---

### 4.3 Grafiek 3: Maximale Vermogensoverschrijding (Lijndiagram)

**Doel:** Toont het piekvermogen dat de batterij moet kunnen leveren.

| Element | Beschrijving |
|---------|--------------|
| X-as | Datum |
| Y-as | Maximale overschrijding (kW) |
| Lijn | Dagelijkse piekvermogen boven contractlimiet |
| Blauwe stippellijn | Batterijvermogen (${battKw} kW) |

**Betekenis:**
- **Lijn ONDER referentie**: Ontlaadsnelheid batterij is voldoende
- **Lijn BOVEN referentie**: Batterij kan niet snel genoeg ontladen

**Let op:**
Zelfs als de batterij genoeg capaciteit (kWh) heeft, kan onvoldoende vermogen (kW) leiden tot overschrijding. Dit zijn twee onafhankelijke criteria!

---

### 4.4 Grafiek 4: Scatter Plot - Vermogen vs. Energie

**Doel:** Gecombineerde weergave van beide kritieke dimensies.

| Element | Beschrijving |
|---------|--------------|
| X-as | Piekvermogen overschrijding (kW) |
| Y-as | Totale energiebehoefte (kWh) |
| 🟢 Groene punten | Dag geslaagd (beide criteria voldaan) |
| 🔴 Rode punten | Dag gefaald (één of beide criteria niet voldaan) |

**Analysekwadranten:**
| Positie | Interpretatie |
|---------|---------------|
| Links-onder | Ideaal: lage energie- en vermogensbehoefte |
| Rechts-onder | Hoog vermogen, lage energie (korte piek) |
| Links-boven | Lage piek, hoge energie (langdurig verbruik) |
| Rechts-boven | Kritiek: hoge energie EN vermogen |

---

## 5. Resultaatinterpretatie

### 5.1 Success Rate Classificatie

| Range | Status | Aanbeveling |
|-------|--------|-------------|
| ≥ 95% | ✅ Voldoende | Batterijconfiguratie is geschikt |
| 80-95% | ⚠️ Bijna voldoende | Overweeg 10-15% capaciteitsverhoging |
| < 80% | ❌ Onvoldoende | Heroverweeg batterijgrootte of contractlimiet |

### 5.2 Automatische Aanbevelingen
De tool genereert automatisch aanbevelingen gebaseerd op:
- Verhouding energietekort vs. capaciteit
- Verhouding vermogenstekort vs. batterijvermogen
- Seizoenspatronen in de data

---

## 6. Scenario-analyse

### 6.1 Wat is een Scenario?
Een scenario simuleert de impact van:
- **Nieuwe machines**: Extra verbruik toevoegen
- **Vervangende machines**: Bestaand verbruik vervangen door efficiëntere apparatuur
- **Tijdvenster**: Scenario alleen toepassen tijdens bepaalde uren

### 6.2 Baseline vs. Scenario Vergelijking
De tool berekent:
1. **Baseline**: Simulatie met originele data
2. **Met scenario**: Simulatie met aangepaste data (scenario toegepast)
3. **Delta**: Verschil in success rate en geslaagde dagen

---

## 7. Beperkingen & Aannames

### 7.1 Aannames
| Aanname | Toelichting |
|---------|-------------|
| Eenheid van de brondata | Vastgesteld bij het uploaden (keuze, kolomkop of bevestiging), niet afgeleid uit de waarden |
| Batterij volledig opgeladen | Start van piekuren met 100% SOC |
| Geen degradatie | Batterijprestaties constant over analysperiode |
| Perfecte voorspelling | Geen onvoorziene verbruikspieken |
| Round-trip efficiency | Niet meegenomen (typisch 85-95%) |

### 7.2 Beperkingen
- Gebaseerd op historische data; toekomstig verbruik kan afwijken
- Geen rekening met netlimieten of technische storingen
- Uurlijkse granulariteit in sommige grafieken (kwartierdata beschikbaar)
- **Uploads van vóór 8 september 2026** zijn ingelezen zonder eenheidsvraag: de kolomwaarde werd altijd als kW overgenomen. Voor die simulaties is de eenheid een aanname en geen vastgelegd gegeven. Stond het bronbestand in kWh per kwartier, dan zijn piek en energietekort van die run te laag. Bij twijfel: het bronbestand opnieuw uploaden en de eenheid bevestigen
- De vastgestelde eenheid wordt nog niet bij de simulatie opgeslagen, dus in dit document staat hij alleen als hij bij het exporteren wordt meegegeven

### 7.3 Aanbevelingen voor Nauwkeurigheid
- Gebruik minimaal 12 maanden data voor seizoenspatronen
- Valideer data op uitschieters en meetfouten
- Overweeg 10-15% veiligheidsmarge bij batterijselectie

---

## 8. Glossarium

| Term | Definitie |
|------|-----------|
| **kW** | Kilowatt - eenheid van vermogen (momentaan) |
| **kWh** | Kilowattuur - eenheid van energie (vermogen × tijd) |
| **kWh per kwartier** | Energie binnen één kwartier; × 4 geeft het momentane vermogen in kW |
| **C-waarde** | Verhouding tussen batterijvermogen en capaciteit |
| **Contractlimiet** | Maximaal toegestaan vermogen tijdens piekuren |
| **Piekuren** | Tijdvenster waarin contractlimiet geldt |
| **SOC** | State of Charge - laadtoestand batterij |
| **Success Rate** | Percentage dagen waarop batterij voldoet |

---

## 9. Contact & Support

Voor vragen over de Flex-E simulatie of ondersteuning bij interpretatie:

**Greenvision Energy Solutions**
📧 info@greenvision.energy
🌐 www.greenvision.energy

---

*Dit document is automatisch gegenereerd door de Flex-E Simulatietool.*
*Versie: ${FLEXE_METHODOLOGY_VERSION} | Datum: ${new Date().toLocaleDateString('nl-NL')}*
`;
};

export const downloadMethodologyAsMarkdown = (simulationData?: {
  contractLimit?: number;
  battKw?: number;
  battKwh?: number;
  windowStart?: string;
  windowEnd?: string;
  successRate?: number;
  baselineSuccessRate?: number;
  loadUnit?: string;
}) => {
  const markdown = getFlexEMethodologyMarkdown(simulationData);
  const blob = new Blob([markdown], { type: 'text/markdown;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `flexe_methodiek_v${FLEXE_METHODOLOGY_VERSION}.md`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};
